import React from "react";
import { Link } from "react-router-dom";
import { HiExclamationTriangle } from "react-icons/hi2";

/**
 * Whether the account is blocked only because its conversation limit was reached (plan cap or a per-account limit).
 * Accounts blocked before billing set billingBlockReason are recognised by billing's cap message.
 */
export const isConversationLimitReached = account => {
  if (!account || account.testAccount || account.billingStatus !== false) {
    return false;
  }
  if (account.billingBlockReason) {
    return account.billingBlockReason === "conversation-limit";
  }
  return /limit of \d+ conversations reached/.test(account.message || "");
};

/**
 * Warning pinned to the top centre of the console while the conversation limit stops the agents from serving chats.
 * Fixed and floating over the header's empty middle, so it takes no layout space. Not dismissible: it stays until
 * the limit is lifted. `belowImpersonation` moves it under the header when the impersonation pill holds that spot.
 */
export default function ConversationLimitBanner({ account, belowImpersonation = false }) {
  if (!isConversationLimitReached(account)) {
    return null;
  }
  const message = account.message || "Conversation limit reached.";

  return (
    <div
      role="alert"
      className={`fixed left-1/2 z-[60] flex max-w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-1.5 pl-3 pr-4 text-[13px] text-amber-800 shadow-lg dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200 ${belowImpersonation ? "top-16" : "top-2.5"}`}
    >
      <HiExclamationTriangle className="h-4 w-4 flex-none text-amber-500" aria-hidden="true" />
      <span className="min-w-0 truncate" title={message}>
        <span className="font-semibold">Agents stopped replying.</span> {message}
      </span>
      <Link to="/user-settings/subscription/plans" className="flex-none whitespace-nowrap font-semibold underline hover:no-underline">
        Upgrade plan &rarr;
      </Link>
    </div>
  );
}
